import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Eye, TrendingUp } from "lucide-react";
import { categoryLabel } from "@/lib/blog-labels";

interface TopPost {
  id: string;
  title: string;
  slug: string;
  category: string;
  views: number;
  cover_image: string | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  dicas: "bg-blue-100 text-blue-700",
  tendencias: "bg-purple-100 text-purple-700",
  noticias: "bg-yellow-100 text-yellow-700",
  armacoes: "bg-orange-100 text-orange-700",
  lentes: "bg-teal-100 text-teal-700",
  "saude-visual": "bg-green-100 text-green-700",
  negocios: "bg-rose-100 text-rose-700",
};

export function MaisLidos({ limit = 5, columns = 1 }: { limit?: number; columns?: 1 | 2 }) {
  const { data: posts = [], isLoading, isError } = useQuery({
    queryKey: ["mais-lidos", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("id, title, slug, category, views, cover_image")
        .eq("published", true)
        .order("views", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data || []) as TopPost[];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Carregando posts mais lidos...">
        {Array.from({ length: limit }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="h-12 w-12 rounded-lg bg-muted shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) return null;
  if (posts.length === 0) return null;

  return (
    <section aria-label="Posts mais lidos">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-foreground">
          Mais Lidos
        </h2>
      </div>
      <ol className={columns === 2 ? "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" : "space-y-3"}>
        {posts.map((post, i) => (
          <li key={post.id}>
            <Link
              to={`/blog/${post.slug}`}
              className="flex items-start gap-3 group"
              aria-label={`${i + 1}. ${post.title}: ${post.views} visualizações`}
            >
              <span className="font-heading font-black text-2xl text-muted-foreground/30 w-6 shrink-0 leading-none mt-1 select-none">
                {i + 1}
              </span>
              {post.cover_image && (
                <div className="h-12 w-12 rounded-lg overflow-hidden shrink-0 bg-muted">
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    loading="lazy"
                    className="h-full w-full object-contain bg-muted transition-opacity duration-300 group-hover:opacity-90"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = "none";
                      const parent = img.parentElement!;
                      parent.classList.add("flex","items-center","justify-center","bg-gradient-to-br","from-primary/80","to-accent/60");
                      const s = document.createElement("span");
                      s.className = "font-heading font-black text-white/20 text-xs";
                      s.textContent = "AO";
                      parent.appendChild(s);
                    }}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground leading-snug line-clamp-2 group-hover:text-accent transition-colors">
                  {post.title}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${CATEGORY_COLORS[post.category] || "bg-muted text-muted-foreground"}`}>
                    {categoryLabel(post.category)}
                  </span>
                  <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                    <Eye className="h-3 w-3" aria-hidden="true" />
                    {(post.views || 0).toLocaleString("pt-BR")}
                  </span>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
